import { useMemo, useState, type ReactNode } from 'react';
import { orgCrumb } from '../components/OrgLink';
import { useInternalOrgId } from '../hooks/useInternalOrg';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ScreenLink as Link } from '../components/permissions/ScreenLink';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { toast } from '../store/toastStore';
import {
  Pencil, Trash2, Flag, MoreHorizontal, Plus, Lock, Archive, ArchiveRestore, Play, Pause, Clock, SearchX, History, Users,
} from 'lucide-react';

import DeadlineBanner from '../components/DeadlineBanner';
import StatusBadge from '../components/StatusBadge';
import StatusCircles from '../components/StatusCircles';
import ConfirmModal from '../components/ConfirmModal';
import MilestoneFormModal from '../components/projects/MilestoneFormModal';
import MilestoneProgressBar from '../components/projects/MilestoneProgressBar';
import TaskFormModal from '../components/projects/TaskFormModal';
import { useTaskRowActions } from '../components/tasks/useTaskRowActions';
import { DueWithLate } from '../components/tasks/taskColumns';
import { taskPath } from '../components/tasks/taskRowModel';
import { NewWorkLogModal } from '../components/NewWorkLogModal';
import MenuButton from '../components/table/MenuButton';
import type { MenuItemDef } from '../components/ui/Menu';
import Dialog from '../components/ui/Dialog';
import StateBlock from '../components/ui/StateBlock';
import Tabs from '../components/ui/Tabs';
import FilterBar, { type FilterDef } from '../components/ui/FilterBar';
import type { Ticket, MilestoneStatus, Milestone, ActiveTimer } from '../types';
import { apiErrorMessage } from '../components/sales/shared';
import EmptyState from '../components/EmptyState';
import Badge from '../components/ui/Badge';
import DetailHeader from '../components/details/DetailHeader';
import DetailSection from '../components/details/DetailSection';
import DetailSkeleton from '../components/details/DetailSkeleton';
import ForwardLink from '../components/details/ForwardLink';
import { formatDate, formatDateTime } from '../utils/format';
import { useBackParent } from '../hooks/useBackNavigation';
import { useStartTimer } from '../components/home/useHomeData';
import { MILESTONE_STEPS } from '../components/projects/milestoneStatusModel';
import { effectiveProgress } from '../components/timer/progressModel';
import { hoursText } from '../components/projects/projectModel';
import DirIcon from '../components/ui/DirIcon';
import Kpi from '../components/ui/Kpi';
import { undoableDeleteMilestone, DELETE_KINDS } from '../utils/undoableDeletes';
import { useWithoutHidden } from '../utils/undoableActions';
import { useTaskDeleteAction } from '../components/tasks/useTaskDeleteAction';
import { offerUndoStatus, statusIsReversible } from '../utils/undoableActions';

type MsTab = 'tasks' | 'hours' | 'team' | 'history';
const MS_TABS: MsTab[] = ['tasks', 'hours', 'team', 'history'];

// ============================================================
// MilestonePage — P-05/D (D-52, D-56)
//
//   ← חזרה     <לקוח> › <פרויקט> › [<אם> ›] <אבן דרך>
//   [🔒] <שם>   (עיגולי סטטוס)       [✎ עריכה] [+ משימה] [+ תת-אבן דרך] [⋯: אבן דרך חדשה / ארכיון / מחיקה]
//   אחראי · מצוותים · התחלה → יעד (באיחור N ימים) · שעות מוערכות / בפועל
//   התקדמות ███████░░░ N%
//   משימות | שעות | צוות | היסטוריה
//
// Every button shows only with its permission (G, the API's `permissions`):
// [✎] edit, [+ תת-אבן דרך] create (never on a sub-milestone), ⋯ archive /
// restore / delete (P-02a; never a core milestone), the circles clickable for
// who may change the status (an assignee too).
// ============================================================
export default function MilestonePage() {
  const internalOrgId = useInternalOrgId();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as MsTab | null;
  const activeTab: MsTab = tabParam && MS_TABS.includes(tabParam) ? tabParam : 'tasks';
  const setActiveTab = (tab: MsTab) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (tab === 'tasks') next.delete('tab'); else next.set('tab', tab);
    return next;
  });

  const { data: milestoneData, isLoading: milestoneLoading, error: milestoneError, refetch, isFetching } = useQuery({
    queryKey: ['milestone', id],
    queryFn: () => api.getMilestone(id!),
    enabled: !!id,
  });

  const milestone = milestoneData?.data;
  // TopBar "חזרה" on a direct link: up to the parent milestone, else the project.
  useBackParent(milestone?.parent_milestone_id ? `/milestones/${milestone.parent_milestone_id}` : milestone?.project_id ? `/projects/${milestone.project_id}` : null);
  const isStaff = user?.role ? ['admin', 'agent'].includes(user.role) : false;

  const [showCreateSub, setShowCreateSub] = useState(false);
  const [showCreateSibling, setShowCreateSibling] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [addTasksTo, setAddTasksTo] = useState<string | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  // B.23 — completing a milestone does not close its tasks; offer it instead.
  const [openChildren, setOpenChildren] = useState<{ id: string; title: string; status: string }[]>([]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['milestone', id] });
    queryClient.invalidateQueries({ queryKey: ['milestone-tickets', id] });
    queryClient.invalidateQueries({ queryKey: ['milestone-hours', id] });
    queryClient.invalidateQueries({ queryKey: ['milestone-team', id] });
    queryClient.invalidateQueries({ queryKey: ['milestone-history', id] });
    if (milestone?.project_id) queryClient.invalidateQueries({ queryKey: ['project', milestone.project_id] });
  };

  const closeChildrenMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const childId of ids) await api.updateTicketStatus(childId, 'completed');
    },
    onSettled: () => { setOpenChildren([]); refresh(); },
    onError: (err) => toast('error', apiErrorMessage(err, t('common.actionFailed'))),
  });

  // The circles — with an optimistic update.
  const statusMutation = useMutation({
    mutationFn: (newStatus: MilestoneStatus) => api.updateMilestone(id!, { status: newStatus }),
    onMutate: async (newStatus) => {
      await queryClient.cancelQueries({ queryKey: ['milestone', id] });
      const previous = queryClient.getQueryData<any>(['milestone', id]);
      if (previous?.data) queryClient.setQueryData(['milestone', id], { ...previous, data: { ...previous.data, status: newStatus } });
      return { previous };
    },
    onSuccess: (res: any, newStatus, context) => {
      const children = res?.data?.open_children ?? [];
      if (newStatus === 'completed' && children.length > 0) setOpenChildren(children);
      // P-27 (D-166 s.11): "בטל" sends the previous status through the same route. Not
      // offered into / out of "הושלם" (it fires completion events and notifications).
      const prev = context?.previous?.data?.status as MilestoneStatus | undefined;
      if (statusIsReversible(prev, newStatus, ['completed'])) {
        offerUndoStatus({
          message: t('undoToast.statusChanged', { status: t(`status.${newStatus}`, { defaultValue: newStatus }) }),
          revert: () => api.updateMilestone(id!, { status: prev! }),
          onReverted: refresh,
        });
      }
    },
    onError: (err: unknown, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(['milestone', id], context.previous);
      toast('error', apiErrorMessage(err, t('common.error')));
    },
    onSettled: () => refresh(),
  });

  const lifecycle = useMutation({
    mutationFn: async (kind: 'archive' | 'restore') => {
      if (kind === 'archive') await api.archiveMilestone(id!);
      else await api.restoreMilestone(id!);
      return kind;
    },
    onSuccess: (kind) => {
      setShowDelete(false);
      refresh();
      // P-27: an archive happens at once and "בטל" restores it through the server.
      if (kind === 'archive') offerUndoStatus({ message: t('project.milestoneArchived'), revert: () => api.restoreMilestone(id!), onReverted: refresh });
      else toast('success', t('project.milestoneRestored'));
    },
    onError: (err) => { setShowDelete(false); toast('error', apiErrorMessage(err, t('project.actionFailed'))); },
  });

  // P-27 (D-166 s.11): the delete leaves the server after 6 s; "בטל" cancels it and brings
  // the milestone page back (a refusal does too, with the same error toast).
  const requestDelete = () => {
    if (!milestone) return;
    const milestoneId = id!;
    const here = `/milestones/${milestoneId}`;
    setShowDelete(false);
    undoableDeleteMilestone({
      qc: queryClient, id: milestoneId,
      onRestore: () => navigate(here, { replace: true }),
      onError: (err) => {
        toast('error', apiErrorMessage(err, t('project.actionFailed')));
        navigate(here, { replace: true });
      },
    });
    navigate(milestone.parent_milestone_id ? `/milestones/${milestone.parent_milestone_id}` : milestone.project_id ? `/projects/${milestone.project_id}` : '/milestones', { replace: true });
  };

  if (milestoneLoading) return <DetailSkeleton kpis={0} />;

  if (!milestone) {
    const back = <Link to="/milestones" className="btn btn-secondary">{t('common.backToMilestones')}</Link>;
    return (
      <DetailSection>
        {milestoneError ? (
          <StateBlock kind="error" size="page" error={milestoneError} title={t('milestonePage.loadError')} onRetry={() => refetch()} retrying={isFetching} action={back} />
        ) : (
          <EmptyState icon={Flag} title={t('milestonePage.notFound')} action={back} />
        )}
      </DetailSection>
    );
  }

  const perms = milestone.permissions ?? { can_create: false, can_edit: false, can_delete: false, can_change_status: false };
  const isSub = !!milestone.parent_milestone_id;
  const isCore = !!milestone.is_core;
  const archived = !!milestone.archived_at;
  const metrics = milestone.metrics;
  const subs = milestone.sub_milestones ?? [];

  const breadcrumbItems = [
    ...(milestone.organization_name ? [orgCrumb(milestone.organization_id, milestone.organization_name, internalOrgId, t('engagementChain.internalWork'))] : []),
    ...(milestone.project_name ? [{ label: milestone.project_name, href: `/projects/${milestone.project_id}` }] : []),
    ...(isSub && milestone.parent_milestone_name ? [{ label: milestone.parent_milestone_name, href: `/milestones/${milestone.parent_milestone_id}` }] : []),
    { label: milestone.name || t('milestone.milestoneLabel') },
  ];

  // P-02a (D-22, D-25) + A2 — the lifecycle item in ⋯.
  const hasWork = (metrics?.task_count ?? 0) > 0 || (metrics?.actual_hours ?? 0) > 0;
  const hasSubs = subs.length > 0;
  const lifecycleItems: MenuItemDef[] = !perms.can_delete || isCore ? [] : archived
    ? [{ key: 'restore', label: t('project.restoreMilestoneAction'), icon: <ArchiveRestore size={16} strokeWidth={1.75} />, disabled: lifecycle.isPending, onSelect: () => lifecycle.mutate('restore') }]
    : [
        { key: 'archive', label: t('project.archiveMilestoneAction'), icon: <Archive size={16} strokeWidth={1.75} />, disabled: lifecycle.isPending, onSelect: () => lifecycle.mutate('archive') },
        {
          key: 'delete', label: t('milestonePage.delete'), icon: <Trash2 size={16} strokeWidth={1.75} />, danger: true, separatorBefore: true,
          disabled: hasWork || hasSubs, hint: hasSubs ? t('milestonePage.deleteHasSubs') : hasWork ? t('milestonePage.deleteHasWork') : undefined,
          onSelect: () => setShowDelete(true),
        },
      ];

  // P-22 (D-156 #1) — "אבן דרך חדשה" at the same level lives in ⋯ (the main button stays "+ משימה", D-52).
  const canAddSibling = perms.can_create && !archived && !!milestone.project_id;
  const moreItems: MenuItemDef[] = [
    ...(canAddSibling ? [{ key: 'sibling', label: t('milestonePage.addSibling'), icon: <Plus size={16} strokeWidth={1.75} />, onSelect: () => setShowCreateSibling(true) }] : []),
    ...lifecycleItems.map((it, i) => (i === 0 && canAddSibling ? { ...it, separatorBefore: true } : it)),
  ];

  const canAddTask = isStaff && !!milestone.project_id && !archived;
  const canAddSub = perms.can_create && !isSub && !archived && !!milestone.project_id;
  const dash = <span className="text-surface-400">—</span>;
  const sep = <span className="text-surface-300" aria-hidden="true">·</span>;
  const assigneeNames = (milestone.milestone_assignees ?? []).map((a) => a.name).filter(Boolean);
  const plannedText = metrics && metrics.planned_hours !== null ? hoursText(metrics.planned_hours) : (metrics?.size_masked ? '—' : t('projectPage.notDefined'));
  const overdue = metrics?.overdue_days ?? null;

  return (
    <div className="max-w-7xl min-w-0 space-y-6">
      <DetailHeader
        breadcrumb={breadcrumbItems}
        title={(
          <span className="inline-flex items-center gap-2">
            {isCore && <Lock size={18} className="text-text-3" aria-label={t('project.coreMilestone')} />}
            {milestone.name || t('milestone.milestoneLabel')}
          </span>
        )}
        badges={(
          <>
            {isSub && <Badge>{t('milestonePage.subBadge')}</Badge>}
            {archived && <Badge>{t('project.archivedBadge')}</Badge>}
          </>
        )}
        actions={(
          <>
            {perms.can_edit && (
              <button type="button" onClick={() => setShowEdit(true)} className="btn btn-secondary">
                <Pencil size={16} aria-hidden="true" /> {t('milestonePage.edit')}
              </button>
            )}
            {canAddTask && (
              <button type="button" onClick={() => setShowCreateTask(true)} className="btn btn-primary">
                <Plus size={16} aria-hidden="true" /> {t('milestonePage.addTask')}
              </button>
            )}
            {canAddSub && (
              <button type="button" onClick={() => setShowCreateSub(true)} className="btn btn-secondary">
                <Plus size={16} aria-hidden="true" /> {t('milestonePage.addSub')}
              </button>
            )}
            {moreItems.length > 0 && (
              <MenuButton items={moreItems} label={t('uxDetails.moreActions')} triggerClassName="btn btn-secondary px-2.5" showTooltip>
                <MoreHorizontal size={18} aria-hidden="true" />
              </MenuButton>
            )}
          </>
        )}
      >
        <div className="space-y-2 text-sm text-surface-700">
          <StatusCircles
            status={milestone.status}
            onChange={perms.can_change_status && !archived ? (s) => statusMutation.mutate(s) : undefined}
            pending={statusMutation.isPending}
            label={t('milestonePage.statusOf', { name: milestone.name })}
          />
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{t('milestonePage.responsible')}: {milestone.responsible_name ?? dash}</span>
            {sep}
            <span>{t('milestonePage.assignees')}: {assigneeNames.length > 0 ? assigneeNames.join(', ') : dash}</span>
            {sep}
            <span className="tabular-nums">
              {milestone.start_date ? formatDate(milestone.start_date) : '—'} <DirIcon name="forward" size={14} className="inline-block align-middle text-surface-400" aria-hidden /> {milestone.due_date ? formatDate(milestone.due_date) : '—'}
              {overdue !== null && overdue > 0 && <span className="ms-1 font-medium text-danger-700">({t('projectPage.lateBy', { count: overdue })})</span>}
            </span>
            {sep}
            <span>{t('milestonePage.hoursLine')}: <span className="tabular-nums">{plannedText} / {hoursText(metrics?.actual_hours ?? 0)}</span></span>
          </p>
          <div className="flex max-w-md items-center gap-3">
            <span className="text-xs text-surface-600">{t('projectPage.progress')}</span>
            <MilestoneProgressBar value={metrics?.progress ?? 0} label={milestone.name} size="md" className="flex-1" />
          </div>
          {milestone.description && <p className="max-w-3xl text-sm text-surface-600">{milestone.description}</p>}
        </div>
      </DetailHeader>

      <DeadlineBanner dueDate={milestone.due_date} source="milestone" warningDays={milestone.deadline_warning_days || 7} entityLabel={t('milestone.theMilestone')} />

      <Tabs
        ariaLabel={t('milestonePage.sections')}
        value={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'tasks', label: t('milestonePage.tabs.tasks'), count: metrics ? metrics.task_count : undefined },
          { key: 'hours', label: t('milestonePage.tabs.hours') },
          { key: 'team', label: t('milestonePage.tabs.team') },
          { key: 'history', label: t('milestonePage.tabs.history') },
        ]}
      />
      <div role="tabpanel" aria-label={t(`milestonePage.tabs.${activeTab}`)}>
        {activeTab === 'tasks' && (
          <MilestoneTasksTab milestoneId={id!} subs={subs} organizationId={milestone.organization_id} canAddTask={canAddTask} canFillEstimate={perms.can_edit === true} onAddTask={() => setShowCreateTask(true)} />
        )}
        {activeTab === 'hours' && <MilestoneHoursTab milestoneId={id!} projectId={milestone.project_id} />}
        {activeTab === 'team' && <MilestoneTeamTab milestoneId={id!} />}
        {activeTab === 'history' && <MilestoneHistoryTab milestoneId={id!} />}
      </div>

      <Dialog
        open={openChildren.length > 0}
        onClose={() => setOpenChildren([])}
        title={t('timeTasks.milestoneChildren.title')}
        description={t('timeTasks.milestoneChildren.help', { count: openChildren.length })}
        size="md"
        dismissible={!closeChildrenMutation.isPending}
        footer={(
          <>
            <button type="button" onClick={() => setOpenChildren([])} className="btn btn-secondary" disabled={closeChildrenMutation.isPending}>{t('milestonePage.keepTasksOpen')}</button>
            <button type="button" onClick={() => closeChildrenMutation.mutate(openChildren.map((c) => c.id))} disabled={closeChildrenMutation.isPending} aria-busy={closeChildrenMutation.isPending || undefined} className="btn btn-primary">
              {t('timeTasks.milestoneChildren.closeAll')}
            </button>
          </>
        )}
      >
        <ul className="max-h-48 overflow-y-auto divide-y divide-border">
          {openChildren.map((c) => (
            <li key={c.id} className="flex items-center gap-2 py-2 text-sm">
              <bdi className="flex-1 truncate">{c.title}</bdi>
              <StatusBadge status={c.status as any} />
            </li>
          ))}
        </ul>
      </Dialog>

      {(showCreateTask || addTasksTo) && milestone.project_id && (
        <TaskFormModal
          mode="create"
          placement={{ organizationId: milestone.organization_id ?? undefined, projectId: milestone.project_id, milestoneId: addTasksTo ?? milestone.id }}
          lock="milestone"
          onClose={() => { setShowCreateTask(false); setAddTasksTo(null); }}
          onCreated={() => { refresh(); queryClient.invalidateQueries({ queryKey: ['all-tasks'] }); setShowCreateTask(false); setAddTasksTo(null); }}
        />
      )}

      {showCreateSub && milestone.project_id && (
        <MilestoneFormModal
          variant="create"
          projectId={milestone.project_id}
          organizationId={milestone.organization_id}
          customerName={milestone.organization_name}
          projectName={milestone.project_name}
          parentId={milestone.id}
          onClose={() => setShowCreateSub(false)}
          onDone={(ms, addTasks) => {
            refresh();
            queryClient.invalidateQueries({ queryKey: ['milestones', milestone.project_id] });
            setShowCreateSub(false);
            if (addTasks && ms?.id) setAddTasksTo(ms.id);
          }}
        />
      )}

      {showCreateSibling && milestone.project_id && (
        <MilestoneFormModal
          variant="create"
          projectId={milestone.project_id}
          organizationId={milestone.organization_id}
          customerName={milestone.organization_name}
          projectName={milestone.project_name}
          parentId={milestone.parent_milestone_id ?? undefined}
          onClose={() => setShowCreateSibling(false)}
          onDone={(ms, addTasks) => {
            refresh();
            queryClient.invalidateQueries({ queryKey: ['milestones', milestone.project_id] });
            setShowCreateSibling(false);
            if (addTasks && ms?.id) setAddTasksTo(ms.id);
          }}
        />
      )}

      {showEdit && (
        <MilestoneFormModal
          variant="edit"
          milestone={milestone as any}
          organizationId={milestone.organization_id}
          customerName={milestone.organization_name}
          projectName={milestone.project_name}
          onClose={() => setShowEdit(false)}
          onDone={(_ms, addTasks) => {
            refresh();
            queryClient.invalidateQueries({ queryKey: ['milestones', milestone.project_id] });
            setShowEdit(false);
            if (addTasks) setAddTasksTo(milestone.id);
          }}
        />
      )}

      {showDelete && (
        <ConfirmModal
          title={t('milestonePage.deleteTitle')}
          description={t('milestonePage.deleteConfirmV2', { name: milestone.name })}
          confirmLabel={t('milestonePage.deleteAction')}
          cancelLabel={t('common.cancel')}
          onConfirm={requestDelete}
          onCancel={() => setShowDelete(false)}
          danger
        />
      )}
    </div>
  );
}

// ============================================================
// "משימות": the milestone's own tasks, then one group per sub-milestone.
// Columns: ▶ / name / assignees / start / due / status / progress / ⋯.
// ============================================================
function MilestoneTasksTab({ milestoneId, subs, canAddTask, canFillEstimate = false, onAddTask }: {
  milestoneId: string;
  subs: (Milestone & { metrics?: any })[];
  organizationId: string;
  canAddTask: boolean;
  /** D-328 - the milestone's `permissions.can_edit`: shows the subtle "בלי הערכה" mark. */
  canFillEstimate?: boolean;
  onAddTask: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const startTimer = useStartTimer();
  // G — the tab's search and filters live in the URL (back restores them).
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('q') ?? '';
  const status = searchParams.get('status') ?? '';
  const assignee = searchParams.get('assignee') ?? '';
  const setParam = (key: string) => (value: string) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (value) next.set(key, value); else next.delete(key);
    return next;
  }, { replace: true });
  const setSearch = setParam('q');
  const setStatus = setParam('status');
  const setAssignee = setParam('assignee');
  const [logging, setLogging] = useState<{ id: string; title: string } | null>(null);
  const [editingTask, setEditingTask] = useState<any>(null);
  const onFillEstimate = canFillEstimate ? (tk: Ticket) => setEditingTask(tk) : undefined;
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['milestone-tickets', milestoneId] });
    queryClient.invalidateQueries({ queryKey: ['milestone', milestoneId] });
  };
  // P-06/E2 (D-62, D-44) — the same ⋯ as every task row (📎, logs, edit, cancel) + "רישום שעות".
  const taskDelete = useTaskDeleteAction({ onChanged: () => refresh() }); // DQ-178: "מחיקה" for a task manager, as in the other task lists
  const rowKit = useTaskRowActions({
    onChanged: refresh,
    extra: (tk) => [
      { key: 'log', label: t('tasks.list.logTime'), icon: <Clock size={16} strokeWidth={1.75} />, onSelect: () => setLogging({ id: tk.id, title: tk.title }) },
      ...taskDelete.items(tk as any),
    ],
  });
  const menuOf = (tk: Ticket): MenuItemDef[] => rowKit.rowActions(tk as any).filter((a) => a.key !== 'timer');

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['milestone-tickets', milestoneId],
    queryFn: () => api.getMilestoneTickets(milestoneId, { limit: 500, include_sub_milestones: true }),
  });
  const allTickets: Ticket[] = useMemo(() => data?.data ?? [], [data]);
  const tickets: Ticket[] = useWithoutHidden(DELETE_KINDS.ticket, allTickets);

  const people = useMemo(() => {
    const map = new Map<string, string>();
    for (const tk of tickets) {
      for (const a of ((tk as any).agents ?? []) as { id: string; name: string }[]) map.set(a.id, a.name);
      if (tk.assigned_agent_id && tk.agent_name) map.set(tk.assigned_agent_id, tk.agent_name);
    }
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [tickets]);

  const match = (tk: Ticket) => {
    if (search && !tk.title.toLowerCase().includes(search.trim().toLowerCase())) return false;
    if (status && tk.status !== status) return false;
    if (assignee && !(((tk as any).agents ?? []) as { id: string }[]).some((a) => a.id === assignee) && tk.assigned_agent_id !== assignee) return false;
    return true;
  };
  const msOf = (tk: Ticket) => (tk as Ticket & { milestone_id?: string | null }).milestone_id;
  const own = tickets.filter((tk) => msOf(tk) === milestoneId && match(tk));
  const groups = subs.map((s) => ({ sub: s, tasks: tickets.filter((tk) => msOf(tk) === s.id && match(tk)) }));
  const filtering = !!(search || status || assignee);
  const reset = () => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('q'); n.delete('status'); n.delete('assignee'); return n; }, { replace: true });

  const filterDefs: FilterDef[] = [
    { key: 'status', label: t('project.filterByStatus'), value: status, allLabel: t('project.allStatuses'), options: MILESTONE_STEPS.map((s) => ({ value: s, label: t(`milestoneStatus.${s}`) })), onChange: setStatus },
    { key: 'assignee', kind: 'search-select', label: t('project.filterByWorker'), value: assignee, allLabel: t('project.allWorkers'), options: people, onChange: setAssignee },
  ];

  if (isLoading) return <StateBlock kind="loading" />;
  if (error) return <StateBlock kind="error" error={error} title={t('milestonePage.loadError')} onRetry={() => refetch()} retrying={isFetching} />;

  return (
    <section className="space-y-3 min-w-0 max-w-full" aria-label={t('milestonePage.tabs.tasks')}>
      <div className="flex flex-wrap items-start gap-3">
        <FilterBar
          className="min-w-0 flex-1"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t('milestonePage.searchTasks')}
          filters={filterDefs}
          inlineFilters={0}
          onClearFilters={reset}
          onClearAll={reset}
        />
        {canAddTask && (
          <button type="button" className="btn btn-secondary" onClick={onAddTask}>
            <Plus size={16} aria-hidden="true" /> {t('milestonePage.addTask')}
          </button>
        )}
      </div>

      <div className="min-w-0 max-w-full overflow-x-auto rounded-xl border border-border bg-surface">
        <table className="table-modern w-full">
          <thead>
            <tr>
              <th className="w-10"><span className="sr-only">{t('milestonePage.startWork')}</span></th>
              <th>{t('project.task')}</th>
              <th className="hidden md:table-cell">{t('project.assignedTo')}</th>
              <th className="hidden md:table-cell text-end">{t('project.startDate')}</th>
              <th className="text-end">{t('tickets.dueDate')}</th>
              <th>{t('project.status')}</th>
              <th className="hidden sm:table-cell">{t('projectPage.progress')}</th>
              <th className="w-10"><span className="sr-only">{t('uxDetails.moreActions')}</span></th>
            </tr>
          </thead>
          <tbody>
            {own.length === 0 && groups.every((g) => g.tasks.length === 0) && (
              <tr>
                <td colSpan={8} className="py-6 text-center text-sm text-text-2">
                  {filtering ? <span className="inline-flex items-center gap-2"><SearchX size={16} aria-hidden="true" /> {t('project.noTasksMatch')}</span> : t('milestonePage.noRelatedTasks')}
                </td>
              </tr>
            )}
            {own.map((tk) => <TaskRow key={tk.id} tk={tk} onStart={() => startTimer.mutate({ id: tk.id, title: tk.title })} startPending={startTimer.isPending} running={rowKit.runningOf(tk.id)} onPause={rowKit.pauseTimer} pausing={rowKit.pausing} items={menuOf(tk)} statusCell={rowKit.statusCell} onFillEstimate={onFillEstimate} />)}
            {groups.map(({ sub, tasks }) => (
              <SubGroup key={sub.id} sub={sub} tasks={tasks} milestoneId={milestoneId}
                onStart={(tk) => startTimer.mutate({ id: tk.id, title: tk.title })} startPending={startTimer.isPending}
                runningOf={rowKit.runningOf} onPause={rowKit.pauseTimer} pausing={rowKit.pausing}
                menuOf={menuOf} statusCell={rowKit.statusCell} onFillEstimate={onFillEstimate} />
            ))}
          </tbody>
        </table>
      </div>

      {rowKit.dialogs}
      {editingTask && (
        <TaskFormModal
          mode="edit"
          variant="project"
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onSaved={() => { refresh(); setEditingTask(null); }}
        />
      )}
      {taskDelete.dialogs}
      {logging && (
        <NewWorkLogModal
          initialTab="tasks"
          initialItem={{ id: logging.id, title: logging.title } as Ticket}
          onClose={() => setLogging(null)}
          onSuccess={() => { setLogging(null); queryClient.invalidateQueries({ queryKey: ['milestone', milestoneId] }); }}
        />
      )}
    </section>
  );
}

function SubGroup({ sub, tasks, milestoneId, onStart, startPending, runningOf, onPause, pausing, menuOf, statusCell, onFillEstimate }: {
  onFillEstimate?: (tk: Ticket) => void;
  sub: Milestone & { metrics?: any };
  tasks: Ticket[];
  milestoneId: string;
  onStart: (tk: Ticket) => void;
  startPending: boolean;
  runningOf: (taskId: string) => ActiveTimer | null;
  onPause: (timer: ActiveTimer) => void;
  pausing: boolean;
  menuOf: (tk: Ticket) => MenuItemDef[];
  statusCell: (tk: Ticket) => ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <>
      <tr className="bg-surface-subtle" data-testid="sub-milestone-group">
        <td />
        <td colSpan={5} className="text-sm font-semibold text-text">
          <Link to={`/milestones/${sub.id}?from=/milestones/${milestoneId}`} className="hover:text-accent-text hover:underline underline-offset-2">
            ↳ <bdi>{sub.name}</bdi>
          </Link>
          <span className="ms-2 align-middle"><StatusBadge status={sub.status as any} /></span>
        </td>
        <td className="hidden sm:table-cell"><MilestoneProgressBar value={sub.metrics?.progress ?? 0} label={sub.name} className="w-28" /></td>
        <td />
      </tr>
      {tasks.length === 0 ? (
        <tr><td /><td colSpan={7} className="ps-6 text-sm text-text-2">{t('project.noTasksInMilestone')}</td></tr>
      ) : tasks.map((tk) => (
        <TaskRow key={tk.id} tk={tk} indent onStart={() => onStart(tk)} startPending={startPending} running={runningOf(tk.id)} onPause={onPause} pausing={pausing} items={menuOf(tk)} statusCell={statusCell} onFillEstimate={onFillEstimate} />
      ))}
    </>
  );
}

export function TaskRow({ tk, indent = false, onStart, startPending, running = null, onPause, pausing = false, items, statusCell, onFillEstimate }: {
  tk: Ticket;
  onFillEstimate?: (tk: Ticket) => void;
  indent?: boolean;
  onStart: () => void;
  startPending: boolean;
  /** D-332: this task's running work timer - the row then shows pause instead of start. */
  running?: ActiveTimer | null;
  onPause?: (timer: ActiveTimer) => void;
  pausing?: boolean;
  /** P-06/E2 — the shared ⋯ of a task row (`useTaskRowActions`). */
  items: MenuItemDef[];
  statusCell: (tk: Ticket) => ReactNode;
}) {
  const { t } = useTranslation();
  const done = tk.status === 'completed' || !!tk.cancelled_at;
  const agents = (((tk as any).agents ?? []) as { id: string; name: string }[]).map((a) => a.name);
  const names = agents.length > 0 ? agents.join(', ') : tk.agent_name || '—';
  return (
    <tr className="dt-row relative isolate">
      <td>
        {!done && running && onPause && (
          <button type="button" className="btn-icon text-accent-text" onClick={() => onPause(running)} disabled={pausing} aria-label={t('timer.pauseFor', { title: tk.title })} title={t('timer.pause')} data-testid="task-row-pause">
            <Pause size={16} aria-hidden="true" />
          </button>
        )}
        {!done && !(running && onPause) && (
          <button type="button" className="btn-icon text-text-3 hover:text-accent-text" onClick={onStart} disabled={startPending} aria-label={t('project.startWorkOn', { title: tk.title })}>
            <Play size={16} aria-hidden="true" />
          </button>
        )}
      </td>
      <td className={indent ? 'ps-6' : ''}>
        <Link to={taskPath(tk.id)} className={`font-medium hover:text-accent-text hover:underline underline-offset-2 ${done ? 'text-text-2 line-through decoration-surface-400' : 'text-text'}`}>
          <bdi>{tk.title}</bdi>
        </Link>
        {tk.cancelled_at && <Badge tone="danger" className="ms-2">{t('tickets.cancelledLabel')}</Badge>}
        {onFillEstimate && !done && !(Number((tk as any).estimated_hours) > 0) && (
          <button type="button" className="ms-2 text-xs text-text-disabled underline-offset-2 hover:underline" data-testid="task-no-estimate" onClick={() => onFillEstimate(tk)}>{t('tasks.list.noEstimateMark')}</button>
        )}
      </td>
      <td className="hidden max-w-44 truncate md:table-cell text-text-2">{names}</td>
      <td className="hidden md:table-cell text-end tabular-nums whitespace-nowrap text-text-2">{formatDate((tk as Ticket & { start_date?: string | null }).start_date)}</td>
      <td className="text-end tabular-nums whitespace-nowrap text-text-2"><DueWithLate task={tk as any} t={t} /></td>
      <td>{statusCell(tk)}</td>
      <td className="hidden sm:table-cell"><MilestoneProgressBar value={tk.cancelled_at ? 0 : effectiveProgress(tk.status, tk.progress_percent)} label={tk.title} className="w-28" /></td>
      <td>
        <MenuButton items={items} label={t('milestonePage.taskActions', { name: tk.title })} triggerClassName="btn-icon text-text-3 hover:bg-surface-subtle hover:text-text">
          <MoreHorizontal size={14} aria-hidden="true" />
        </MenuButton>
      </td>
    </tr>
  );
}

// ============================================================
// "שעות": estimated vs actual, by employee, and a link to the work records.
// ============================================================
function MilestoneHoursTab({ milestoneId, projectId }: { milestoneId: string; projectId: string | null }) {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['milestone-hours', milestoneId],
    queryFn: () => api.getMilestoneHours(milestoneId),
  });
  if (isLoading) return <StateBlock kind="loading" />;
  if (error || !data?.data) return <StateBlock kind="error" error={error} title={t('milestonePage.loadError')} onRetry={() => refetch()} retrying={isFetching} />;
  const h = data.data;
  const th = 'px-4 py-2 text-start text-xs font-medium text-surface-600';
  const td = 'px-4 py-2 text-sm text-surface-900';
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Kpi label={t('milestonePage.hours.planned')} value={h.planned_hours === null ? t('projectPage.notDefined') : hoursText(h.planned_hours)} />
        <Kpi label={t('milestonePage.hours.actual')} value={hoursText(h.actual_hours)} />
        <Kpi label={t('milestonePage.hours.difference')} value={h.planned_hours === null ? '—' : hoursText(h.planned_hours - h.actual_hours)} />
      </div>
      <DetailSection
        title={t('milestonePage.hours.byEmployee')}
        actions={projectId ? <ForwardLink to={`/work-logs?project=${projectId}`}>{t('projectPage.hours.allRecords')}</ForwardLink> : undefined}
        flush
      >
        {h.by_employee.length === 0 ? (
          <EmptyState icon={Clock} title={t('projectPage.hours.none')} />
        ) : (
          <table className="w-full">
            <caption className="sr-only">{t('milestonePage.hours.byEmployee')}</caption>
            <thead className="border-b border-surface-200">
              <tr>
                <th scope="col" className={th}>{t('projectPage.hours.employee')}</th>
                <th scope="col" className={`${th} text-end`}>{t('milestonePage.hours.estimated')}</th>
                <th scope="col" className={`${th} text-end`}>{t('milestonePage.hours.actual')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-100">
              {h.by_employee.map((e) => (
                <tr key={e.user_id}>
                  <td className={td}>{e.name ?? '—'}</td>
                  <td className={`${td} text-end tabular-nums`}>{hoursText(e.estimated_hours)}</td>
                  <td className={`${td} text-end tabular-nums`}>{hoursText(e.actual_hours)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DetailSection>
    </div>
  );
}

// ============================================================
// "צוות": the responsible, the assignees, the people on its tasks — with hours.
// ============================================================
function MilestoneTeamTab({ milestoneId }: { milestoneId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['milestone-team', milestoneId],
    queryFn: () => api.getMilestoneTeam(milestoneId),
  });
  if (isLoading) return <StateBlock kind="loading" />;
  if (error) return <StateBlock kind="error" error={error} title={t('milestonePage.loadError')} onRetry={() => refetch()} retrying={isFetching} />;
  const people = data?.data ?? [];
  return (
    <DetailSection title={t('milestonePage.tabs.team')} flush>
      {people.length === 0 ? (
        <EmptyState icon={Users} title={t('milestonePage.team.none')} />
      ) : (
        <ul className="divide-y divide-surface-100">
          {people.map((p) => (
            <li key={p.user_id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 sm:px-5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-surface-900">{p.name}</p>
                <p className="mt-0.5 flex flex-wrap gap-1">
                  {p.roles.map((r) => <Badge key={r}>{t(`milestonePage.team.roles.${r}`)}</Badge>)}
                </p>
              </div>
              <span className="text-sm tabular-nums text-surface-700">{t('milestonePage.team.hours', { hours: hoursText(p.hours) })}</span>
            </li>
          ))}
        </ul>
      )}
    </DetailSection>
  );
}

// ============================================================
// "היסטוריה": the milestone's audit rows.
// ============================================================
const HISTORY_FIELDS = ['name', 'status', 'description', 'responsible_user_id', 'start_date', 'due_date', 'planned_hours_mode', 'planned_hours_manual', 'parent_milestone_id', 'archived_at'];

function MilestoneHistoryTab({ milestoneId }: { milestoneId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['milestone-history', milestoneId],
    queryFn: () => api.getMilestoneHistory(milestoneId),
  });
  if (isLoading) return <StateBlock kind="loading" />;
  if (error) return <StateBlock kind="error" error={error} title={t('milestonePage.loadError')} onRetry={() => refetch()} retrying={isFetching} />;
  const rows = data?.data ?? [];
  const valueText = (field: string, v: unknown): string => {
    if (v === null || v === undefined || v === '') return '—';
    if (field === 'status') return t(`milestoneStatus.${String(v)}`);
    if (field === 'planned_hours_mode') return t(`milestoneForm.plannedModes.${String(v)}`);
    if (field.endsWith('_date') || field === 'archived_at') return formatDate(String(v));
    return String(v);
  };
  return (
    <DetailSection title={t('milestonePage.tabs.history')} flush>
      {rows.length === 0 ? (
        <EmptyState icon={History} title={t('milestonePage.history.none')} />
      ) : (
        <ul className="divide-y divide-surface-100">
          {rows.map((r) => {
            const oldV = r.old_values ?? {};
            const newV = r.new_values ?? {};
            const changed = HISTORY_FIELDS.filter((f) => f in newV && JSON.stringify((oldV as any)[f] ?? null) !== JSON.stringify((newV as any)[f] ?? null));
            const auto = (newV as any).auto === true;
            return (
              <li key={r.id} className="px-4 py-3 text-sm sm:px-5">
                <p className="flex flex-wrap items-center gap-2 text-surface-900">
                  <span className="font-medium">{auto ? t('milestonePage.history.auto') : (r.actor_name ?? t('milestonePage.history.system'))}</span>
                  <span className="text-surface-600">{t(`milestonePage.history.actions.${r.action}`, { defaultValue: r.action })}</span>
                  <span className="text-xs tabular-nums text-surface-500">{formatDateTime(r.created_at)}</span>
                </p>
                {changed.length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-xs text-surface-600">
                    {changed.map((f) => (
                      <li key={f}>
                        {t(`milestonePage.history.fields.${f}`)}: <bdi>{valueText(f, (oldV as any)[f])}</bdi> ← <bdi>{valueText(f, (newV as any)[f])}</bdi>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </DetailSection>
  );
}

